import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button, Col, Container, Form, InputGroup, Row, Table } from "react-bootstrap";
import { Helmet } from "react-helmet-async";
import { useDispatch, useSelector } from "react-redux";

import {
  clearNewMcpKey,
  createMcpKey,
  getMyMcpKeys,
  revokeMcpKey,
} from "../../redux/slices/mcpKeySlice";

import "../../assets/css/forms.css";
import ButtonSpinner from "../../components/UI/ButtonSpinner/ButtonSpinner";
import ConfirmationModal from "../../components/UI/ConfirmationModal/ConfirmationModal";
import FormAlert from "../../components/UI/FormAlert/FormAlert";
import Subheader from "../../components/UI/Subheader/Subheader";

const MCP_SERVER_URL = process.env.REACT_APP_MCP_URL || "http://localhost:8001/mcp";

function formatDate(value) {
  return value ? new Date(value).toLocaleString("pt-BR") : "—";
}

function McpKeyScreen() {
  const dispatch = useDispatch();
  const { keys = [], newKey, loading, error } = useSelector((state) => state.mcpKeySlice);

  const [name, setName] = useState("");
  const [keyToRevoke, setKeyToRevoke] = useState(undefined);

  useEffect(() => {
    dispatch(getMyMcpKeys());
    // Discard any stale one-time token left over from a previous visit.
    dispatch(clearNewMcpKey());
  }, [dispatch]);

  const stdioConfigSnippet = useMemo(() => {
    if (!newKey) {
      return "";
    }
    return JSON.stringify(
      {
        mcpServers: {
          watson: {
            command: "uv",
            args: ["run", "--directory", "/CAMINHO/PARA/watson-monorepo/mcp-desktop", "python", "server_stdio.py"],
            env: {
              WATSON_MCP_URL: MCP_SERVER_URL,
              WATSON_MCP_TOKEN: newKey.token,
            },
          },
        },
      },
      null,
      2
    );
  }, [newKey]);

  const headersConfigSnippet = useMemo(() => {
    if (!newKey) {
      return "";
    }
    return JSON.stringify(
      {
        mcpServers: {
          watson: {
            url: MCP_SERVER_URL,
            headers: { Authorization: `Bearer ${newKey.token}` },
          },
        },
      },
      null,
      2
    );
  }, [newKey]);

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!name.trim()) {
      return;
    }
    dispatch(createMcpKey({ name: name.trim() }));
    setName("");
  };

  const handleConfirmRevoke = useCallback(() => {
    if (keyToRevoke) {
      dispatch(revokeMcpKey({ id: keyToRevoke.id }));
      setKeyToRevoke(undefined);
    }
  }, [dispatch, keyToRevoke]);

  const copyToClipboard = (text) => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text);
    }
  };

  return (
    <>
      <Helmet>
        <title>Gerar chave MCP</title>
      </Helmet>
      <Subheader title="Chaves MCP"></Subheader>

      <Container>
        <Row className="mb-3">
          <Col>
            <h4 className="screen-header-title">Gerar chave MCP</h4>
            <p>
              Gere uma chave para autenticar um cliente MCP (Claude, etc.) no servidor Watson. A chave é exibida{" "}
              <strong>uma única vez</strong>. Revogar uma chave a apaga e desativa a conexão imediatamente.
            </p>
          </Col>
        </Row>

        {newKey && (
          <Row className="mb-4">
            <Col>
              <Alert variant="success">
                <Alert.Heading>Chave criada — salve agora!</Alert.Heading>
                <p>Esta chave não será exibida novamente. Copie-a agora.</p>

                <Form.Label>Token</Form.Label>
                <InputGroup className="mb-3">
                  <Form.Control readOnly value={newKey.token} />
                  <Button variant="outline-secondary" onClick={() => copyToClipboard(newKey.token)}>
                    Copiar
                  </Button>
                </InputGroup>

                <Form.Label>Configuração do Claude Desktop (wrapper local, recomendado)</Form.Label>
                <p className="text-muted small mb-2">
                  Cole em <code>claude_desktop_config.json</code>. Ajuste <code>--directory</code> para onde você
                  clonou o repositório. Veja <code>mcp-desktop/README.md</code> para o passo a passo completo.
                </p>
                <InputGroup className="mb-3">
                  <Form.Control as="textarea" readOnly rows={10} value={stdioConfigSnippet} />
                  <Button variant="outline-secondary" onClick={() => copyToClipboard(stdioConfigSnippet)}>
                    Copiar
                  </Button>
                </InputGroup>

                <Form.Label>Configuração avançada — clientes MCP com suporte a headers customizados</Form.Label>
                <InputGroup>
                  <Form.Control as="textarea" readOnly rows={8} value={headersConfigSnippet} />
                  <Button variant="outline-secondary" onClick={() => copyToClipboard(headersConfigSnippet)}>
                    Copiar
                  </Button>
                </InputGroup>

                <div className="mt-3">
                  <Button variant="secondary" onClick={() => dispatch(clearNewMcpKey())}>
                    Ok, salvei a chave
                  </Button>
                </div>
              </Alert>
            </Col>
          </Row>
        )}

        <Row className="mb-4">
          <Col>
            <div className="registration-form">
              <h4>Criar nova chave</h4>
              <Form onSubmit={handleSubmit}>
                <Form.Group controlId="mcpKeyName" className="mb-3">
                  <Form.Label>Nome da chave</Form.Label>
                  <InputGroup>
                    <Form.Control
                      required
                      placeholder="Ex.: Claude Desktop"
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                    />
                  </InputGroup>
                </Form.Group>

                <ButtonSpinner type="submit" isLoading={loading}>
                  Criar nova chave
                </ButtonSpinner>
              </Form>
            </div>

            {error && <FormAlert alertTextList={error} />}
          </Col>
        </Row>

        <Row>
          <Col>
            <h4>Minhas chaves</h4>
            <Table hover responsive>
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Prefixo</th>
                  <th>Criada em</th>
                  <th>Último uso</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {keys.length === 0 ? (
                  <tr>
                    <td colSpan={5}>Nenhuma chave gerada ainda.</td>
                  </tr>
                ) : (
                  keys.map((key) => (
                    <tr key={key.id}>
                      <td>{key.name}</td>
                      <td>
                        <code>{key.prefix}…</code>
                      </td>
                      <td>{formatDate(key.created_at)}</td>
                      <td>{formatDate(key.last_used_at)}</td>
                      <td className="text-end">
                        <Button variant="outline-danger" size="sm" onClick={() => setKeyToRevoke(key)}>
                          Revogar
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </Col>
        </Row>
      </Container>

      <ConfirmationModal
        show={!!keyToRevoke}
        onHide={() => setKeyToRevoke(undefined)}
        loading={loading}
        buttonPrimaryText="Revogar"
        buttonSecondaryText="Cancelar"
        buttonPrimaryOnClick={handleConfirmRevoke}
        buttonSecondaryOnClick={() => setKeyToRevoke(undefined)}
      >
        <p>
          Tem certeza que deseja revogar a chave <strong>{keyToRevoke?.name}</strong>? Esta ação é permanente e desativa
          qualquer conexão que a utilize.
        </p>
      </ConfirmationModal>
    </>
  );
}

export default McpKeyScreen;
